import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Fade
} from '@mui/material';
import { useApi } from '../hooks/useApi';
import { styled, alpha } from '@mui/material/styles';
import { cyber } from '../theme';
import ArticleIcon from '@mui/icons-material/Article';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Layout container styled for consistency with IOCSearchPage
const PageContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 'calc(100vh - 120px)',
    padding: theme.spacing(3),
    background: theme.palette.background.default
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(5),
    maxWidth: 600,
    width: '100%',
    boxShadow: 'none',
    border: `1px solid ${cyber.border}`,
    borderRadius: `${Number(theme.shape.borderRadius) * 2}px`
}));

const SubmitArticle: React.FC = () => {
    const { poster } = useApi();
    const [url, setUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await poster('/api/submissions/', { url });
            setSuccess(response.message || 'Article submitted successfully.');
            setUrl('');
        } catch (e: any) {
            const errorMessage =
                e.response?.data?.detail ||
                e.message ||
                'An unknown error occurred.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <PageContainer>
            <Fade in timeout={500}>
                <StyledPaper>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <ArticleIcon color="primary" fontSize="large" />
                        <Typography variant="h4" component="h1" fontWeight="bold">
                            Submit an Article
                        </Typography>
                    </Box>

                    <Typography variant="body1" paragraph color="text.secondary">
                        Found an interesting threat intelligence report or blog post?
                        Paste the URL below to submit it for review and potential inclusion
                        in our threat intelligence database.
                    </Typography>

                    <Paper
                        elevation={0}
                        sx={{
                            mb: 3,
                            p: 2,
                            bgcolor: alpha(cyber.accent.cyan, 0.08),
                            border: `1px solid ${alpha(cyber.accent.cyan, 0.3)}`,
                            borderRadius: 1,
                            display: 'flex',
                            gap: 1.5,
                            alignItems: 'flex-start',
                        }}
                    >
                        <InfoOutlinedIcon
                            sx={{ color: cyber.accent.cyan, fontSize: 20, mt: 0.25, flexShrink: 0 }}
                        />
                        <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                <strong>Approval required:</strong> every submitted article is
                                subject to human review before entering the processing pipeline.
                                Please allow some time for approval.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Once approved, you will receive a notification email
                                from <strong>info@ti-mindmap-hub.com</strong> with a direct link
                                to the processed article.
                            </Typography>
                        </Box>
                    </Paper>

                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <TextField
                            fullWidth
                            required
                            id="url"
                            name="url"
                            label="Article URL"
                            placeholder="https://example.com/threat-report"
                            margin="normal"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{
                                mt: 2,
                                mb: 2,
                                py: 1.2,
                                fontWeight: 600,
                                background: cyber.gradient,
                                '&:hover': {
                                    background: cyber.gradientHover,
                                    boxShadow: cyber.glow
                                }
                            }}
                            disabled={isLoading || !url}
                        >
                            {isLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Submit URL'
                            )}
                        </Button>

                        {success && (
                            <Alert
                                severity="success"
                                sx={{
                                    mt: 2,
                                    bgcolor: alpha(cyber.accent.green, 0.12),
                                    border: `1px solid ${alpha(cyber.accent.green, 0.4)}`,
                                    '& .MuiAlert-icon': {
                                        color: cyber.accent.green,
                                    },
                                }}
                            >
                                {success}
                            </Alert>
                        )}
                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </Box>
                </StyledPaper>
            </Fade>
        </PageContainer>
    );
};

export default SubmitArticle;
